import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { TextField, Button, CircularProgress } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle, faInfoCircle } from "@fortawesome/pro-solid-svg-icons";

import type { IPartner, ILabel } from "App/Modules/Models";
import type { IPartnersRepository } from "App/Modules/Repositories";
import { ConfirmDeletion } from "../../ConfirmDeletion";
import "./_labels.scss";

type LabelsDialogProps = {
    onClose: () => void;
    labels: ILabel[];
    chosenLabel: ILabel;
    partnersRepository: IPartnersRepository;
    partner: IPartner;
    onLabelsChange: () => void;
};

export const LabelsDialog = (props: LabelsDialogProps) => {
    const { t } = useTranslation();
    const { onClose, labels, chosenLabel, partnersRepository, partner } = props;
    const [saveError, setSaveError] = useState(false);
    const [isChangesSaved, setIsChangesSaved] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [labelEdit, setLabelEdit] = useState(chosenLabel.label);
    const [changeLabel, setChangeLabel] = useState<ILabel>(null);

    const handleTextChange = (value) => {
        setLabelEdit(value);
    };
    const handleLabelChange = (value) => {
        setChangeLabel(value);
    };

    const handleClose = () => {
        onClose();
    };

    const handleDelete = () => {
        partnersRepository.removeLabel(partner.id, chosenLabel.id).subscribe(
            (response) => {
                if (!response.hasError && response) {
                    props.onLabelsChange();
                    onClose();
                }
            },
            () => {
                setSaveError(true);
            },
        );
    };

    const handleSave = (event: React.MouseEvent) => {
        event.preventDefault();
        setSaveError(false);
        // change name on label
        if (labelEdit !== chosenLabel.label) {
            const newLabelArr = labels.map((object) =>
                object.label === chosenLabel.label ? { ...object, label: labelEdit } : object,
            );
            const label = newLabelArr.find((obj) => {
                return obj.label === labelEdit;
            });

            setIsButtonDisabled(true);
            setIsChangesSaved(true);
            partnersRepository.editLabel(partner.id, label).subscribe(
                (response) => {
                    if (!response.hasError && response) {
                        props.onLabelsChange();
                        onClose();
                    }
                },
                () => {
                    setSaveError(true);
                },
            );
        }

        // merge labebls
        if (changeLabel?.label && changeLabel.label !== chosenLabel.label) {
            setIsButtonDisabled(true);
            setIsChangesSaved(true);
            partnersRepository.replaceLabel(partner.id, changeLabel.id, chosenLabel.id).subscribe(
                (response) => {
                    if (!response.hasError && response) {
                        props.onLabelsChange();
                        onClose();
                    }
                },
                () => {
                    setSaveError(true);
                },
            );
        }
    };

    return (
        <div className="dialog card labels">
            <div className="dialog__section dialog__section--full">
                <h1 className="title title--h1">{t("Entities.Partner.EditUserLabel")}</h1>
                <div className="notice notice--info">
                    <FontAwesomeIcon className="notice__icon" fixedWidth icon={faInfoCircle} />
                    <p className="caption">{t("Entities.Partner.EditUserLabel.Hint")}</p>
                </div>
            </div>
            <div className="dialog__section dialog__section--full">
                <form className="form">
                    <div className="form__row">
                        <p className="caption">{t("Entities.Common.Name")}</p>
                        <TextField
                            fullWidth
                            value={labelEdit}
                            type="text"
                            size="small"
                            onChange={(e) => {
                                handleTextChange(e.target.value);
                            }}
                        />
                    </div>
                    <div className="form__row">
                        <p className="caption">{t("Gui.Manage.Label.DeleteMerge")}</p>
                        <Autocomplete
                            options={labels}
                            value={changeLabel}
                            onChange={(e, value: ILabel) => {
                                handleLabelChange(value);
                            }}
                            getOptionLabel={(option) => option.label || ""}
                            size="small"
                            renderInput={(params) => <TextField {...params} fullWidth />}
                        />
                    </div>
                </form>
            </div>
            {saveError ? (
                <div className="dialog__section dialog__section--full">
                    <div className="notice notice--critical">
                        <FontAwesomeIcon className="notice__icon" fixedWidth icon={faExclamationTriangle} />
                        <p className="caption">{t("Error.ErrorSaving")}</p>
                    </div>
                </div>
            ) : null}
            <div className="dialog__section--buttons labels__buttons">
                <ConfirmDeletion
                    onUserConfirm={handleDelete}
                    titleText={t("Entities.Confirm.Deletion")}
                    bodyText={t("Entities.Confirm.Deletion.AreYouSure")}
                />
                <div>
                    <Button onClick={handleClose}>{t("Gui.Buttons.Cancel")}</Button>
                    <Button
                        onClick={(e: React.MouseEvent) => handleSave(e)}
                        variant="contained"
                        color="primary"
                        disabled={
                            (!changeLabel?.label && labelEdit === chosenLabel.label) ||
                            labelEdit === changeLabel?.label ||
                            isButtonDisabled
                        }
                        startIcon={isChangesSaved ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {t("Gui.Buttons.Save")}
                    </Button>
                </div>
            </div>
        </div>
    );
};
